import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, ArrowRight, Clock, Flag, LogOut } from "lucide-react"
import { useIntl } from "react-intl"
import { AppShell } from "@/components/AppShell"
import { AskAssistantButton } from "@/components/assistant/AskAssistantButton"
import { HelpfulPrompt } from "@/components/quizzes/HelpfulPrompt"
import { CasePanel } from "@/components/exams/CasePanel"
import { clock } from "@/components/exams/format"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { ChoiceOption } from "@/components/quizzes/ChoiceOption"
import { CHOICE_LETTERS } from "@/components/quizzes/format"
import { Button } from "@/components/ui/button"
import { csrfToken } from "@/lib/csrf"
import { useFormat } from "@/lib/format"
import { cn } from "@/lib/utils"
import type { CaseNarrative, ExamAttemptState, ExamQuestion, SectionLabels } from "@/types/exams"

const HEARTBEAT_MS = 30_000
const LOW_TIME_SECONDS = 10 * 60

type ClockResponse = { time_remaining_seconds: number; finished: boolean; results_url: string | null }

type TakeProps = {
  attempt: ExamAttemptState
  exam: { id: number; title: string }
  cases: CaseNarrative[]
  questions: ExamQuestion[]
  section_labels: SectionLabels
}

// Inertia keeps this page mounted across visits (submitting part 1, ending the
// break), so key the session by part to start each part with fresh state.
export default function ExamTake(props: TakeProps) {
  const { attempt } = props
  return <ExamSession key={`${attempt.id}-${attempt.part}-${attempt.on_break}`} {...props} />
}

function ExamSession({ attempt, exam, cases, questions: initialQuestions }: TakeProps) {
  const intl = useIntl()
  const format = useFormat()
  const sectionLabels: SectionLabels = {
    intake: intl.formatMessage({ id: "exams.section_intake" }),
    session_1: intl.formatMessage({ id: "exams.section_session_1" }),
    session_2: intl.formatMessage({ id: "exams.section_session_2" }),
  }
  const full = attempt.mode === "full_exam"
  const [questions, setQuestions] = React.useState(initialQuestions)
  const firstOpen = initialQuestions.findIndex((q) => q.chosen_choice === null)
  const [index, setIndex] = React.useState(firstOpen === -1 ? 0 : firstOpen)
  const [reviewing, setReviewing] = React.useState(false)
  const [confirmSubmit, setConfirmSubmit] = React.useState(false)
  const [selected, setSelected] = React.useState<number | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const remaining = useCountdown(attempt)

  const question = questions[index]
  const caseStudy = cases.find((c) => c.id === question?.case_study_id)
  const partValues = { multi: String(attempt.part_count > 1), part: attempt.part }
  const title = full ? exam.title : intl.formatMessage({ id: "exams.take_title_case" }, { title: caseStudy?.title ?? exam.title })
  const description = intl.formatMessage({ id: "exams.take_meta" }, { title: exam.title })

  const save = async (q: ExamQuestion, body: { choice?: number; flagged?: boolean }) => {
    setSaving(true)
    setError(null)
    try {
      const response = await fetch(`/exam_attempts/${attempt.id}/answer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ question_id: q.id, ...body }),
      })
      const data = await response.json()
      if (full && data.finished && data.results_url) {
        router.visit(data.results_url)
        return
      }
      if (!response.ok) {
        setError(data.error ?? intl.formatMessage({ id: "exams.save_error" }))
        return
      }
      if (data.time_remaining_seconds !== undefined) remaining.sync(data.time_remaining_seconds)
      setQuestions((all) => all.map((x) => (x.id === q.id ? data.question : x)))
    } catch {
      setError(intl.formatMessage({ id: "exams.save_error_network" }))
    } finally {
      setSaving(false)
    }
  }

  const go = (to: number) => {
    setIndex(to)
    setSelected(null)
    setError(null)
    setReviewing(false)
    window.scrollTo({ top: 0 })
  }

  if (attempt.on_break) {
    return (
      <Layout title={title} description={description}>
        <div className="mx-auto max-w-2xl">
          <h1>{exam.title}</h1>
          <div className="callout mt-6 text-center">
            <h2>{intl.formatMessage({ id: "exams.part_submitted" }, { part: attempt.part - 1 })}</h2>
            <p className="mt-2">
              {intl.formatMessage({ id: "exams.break_body" }, { remaining: format.duration(attempt.time_remaining_seconds ?? 0) })}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <Button type="button" variant="secondary" onClick={() => router.post(`/exam_attempts/${attempt.id}/pause`)}>
                <LogOut className="h-4 w-4" /> {intl.formatMessage({ id: "exams.save_exit" })}
              </Button>
              <Button type="button" onClick={() => router.post(`/exam_attempts/${attempt.id}/end_break`)}>
                {intl.formatMessage({ id: "exams.start_part" }, { part: attempt.part })} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const answeredCount = questions.filter((q) => q.chosen_choice !== null).length
  const flaggedCount = questions.filter((q) => q.flagged).length
  const unanswered = questions.length - answeredCount
  const singleDone = !full && answeredCount === questions.length

  return (
    <Layout title={title} description={description}>
      <div className="flex flex-col gap-3 border-b border-hairline pb-4 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1>{full ? exam.title : caseStudy?.title}</h1>
          <p className="mt-1 text-sm text-ink-muted">
            {full
              ? intl.formatMessage({ id: "exams.part_x_of_y" }, { part: attempt.part, total: attempt.part_count })
              : intl.formatMessage({ id: "exams.case_practice_from" }, { title: exam.title })}{" "}
            · {intl.formatMessage({ id: "exams.answered_of" }, { answered: answeredCount, total: questions.length })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {full && remaining.seconds !== null && (
            <div
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-1.5 font-mono text-sm",
                remaining.seconds <= LOW_TIME_SECONDS ? "text-signal-display" : "text-ink-display",
              )}
              role="timer"
              aria-label={intl.formatMessage({ id: "exams.timer_label" }, { remaining: format.duration(remaining.seconds) })}
            >
              <Clock className="h-4 w-4" /> {clock(remaining.seconds)}
            </div>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => router.post(`/exam_attempts/${attempt.id}/pause`)}>
            <LogOut className="h-4 w-4" /> {intl.formatMessage({ id: "exams.save_exit" })}
          </Button>
        </div>
      </div>

      {reviewing ? (
        <section className="mt-6">
          <h2>{intl.formatMessage({ id: "exams.review_label" }, partValues)}</h2>
          <p className="mt-1">
            {intl.formatMessage({ id: "exams.review_summary" }, { answered: answeredCount, total: questions.length, flagged: flaggedCount })}
          </p>
          {unanswered > 0 && (
            <div className="callout callout-signal mt-4">
              <p>
                {intl.formatMessage({ id: "exams.unanswered_warning" }, { count: unanswered })}
              </p>
            </div>
          )}
          <ol className="mt-4 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page p-0">
            {questions.map((q, i) => (
              <li key={q.id} className="list-none">
                <button
                  type="button"
                  onClick={() => go(i)}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left hover:bg-surface"
                >
                  <span className="w-6 shrink-0 text-right text-xs text-ink-muted">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-ink-body">{q.text}</span>
                  {q.flagged && <Flag className="h-4 w-4 shrink-0 text-signal-display" aria-label={intl.formatMessage({ id: "exams.flagged" })} />}
                  <span className={cn("shrink-0 text-xs", q.chosen_choice === null ? "text-signal-display" : "text-ink-muted")}>
                    {q.chosen_choice === null ? intl.formatMessage({ id: "exams.unanswered" }) : intl.formatMessage({ id: "exams.answered_letter" }, { letter: CHOICE_LETTERS[q.chosen_choice] })}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => setReviewing(false)}>
              <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "exams.back_to_questions" })}
            </Button>
            <Button type="button" onClick={() => setConfirmSubmit(true)}>
              {intl.formatMessage({ id: "exams.submit_label" }, partValues)}
            </Button>
          </div>
          <ConfirmDialog
            open={confirmSubmit}
            onOpenChange={setConfirmSubmit}
            title={intl.formatMessage({ id: "exams.submit_title" }, partValues)}
            description={
              attempt.part < attempt.part_count
                ? intl.formatMessage({ id: "exams.confirm_part" }, { part: attempt.part })
                : intl.formatMessage({ id: "exams.confirm_final" })
            }
            confirmLabel={intl.formatMessage({ id: "exams.submit_label" }, partValues)}
            onConfirm={() => router.post(`/exam_attempts/${attempt.id}/submit_half`)}
          />
        </section>
      ) : (
        question &&
        caseStudy && (
          <div className="mt-6 grid gap-6 lg:grid-cols-2 lg:items-start">
            <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
              <CasePanel caseStudy={caseStudy} section={question.section} labels={sectionLabels} />
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-ink-muted">
                  {intl.formatMessage({ id: "quizzes.question_x_of_y" }, { current: index + 1, total: questions.length })}
                </span>
                {full && (
                  <button
                    type="button"
                    className="toggle-button"
                    aria-pressed={question.flagged}
                    disabled={saving}
                    onClick={() => save(question, { flagged: !question.flagged })}
                  >
                    <Flag className="h-3.5 w-3.5" /> {question.flagged ? intl.formatMessage({ id: "exams.flagged" }) : intl.formatMessage({ id: "exams.flag" })}
                  </button>
                )}
              </div>
              <fieldset key={question.id} className="mt-3">
                <legend className="whitespace-pre-wrap break-words">{question.text}</legend>
                <div className="mt-4 space-y-2">
                  {question.choices.map((choice, i) => {
                    const fb = question.feedback
                    const checked = full ? question.chosen_choice === i : (question.chosen_choice ?? selected) === i
                    return (
                      <ChoiceOption
                        key={i}
                        name={`question-${question.id}`}
                        index={i}
                        text={choice}
                        checked={checked}
                        disabled={saving || (!full && question.chosen_choice !== null)}
                        state={!fb ? null : i === fb.correct_choice ? "correct" : i === question.chosen_choice ? "wrong" : null}
                        onSelect={() => (full ? save(question, { choice: i }) : setSelected(i))}
                      />
                    )
                  })}
                </div>
              </fieldset>

              {question.feedback && (
                <div
                  className={cn("callout mt-6", question.feedback.correct ? "callout-accent" : "callout-danger")}
                  role="status"
                >
                  <p>
                    <strong>{question.feedback.correct ? intl.formatMessage({ id: "quizzes.correct" }) : intl.formatMessage({ id: "quizzes.not_quite" })}</strong>{" "}
                    {!question.feedback.correct &&
                      intl.formatMessage({ id: "exams.best_is" }, { letter: CHOICE_LETTERS[question.feedback.correct_choice], choice: question.choices[question.feedback.correct_choice] })}
                  </p>
                  {question.feedback.explanation && (
                    <p className="mt-2 whitespace-pre-wrap">{question.feedback.explanation}</p>
                  )}
                  <div className="mt-3">
                    <HelpfulPrompt key={question.id} attemptId={attempt.id} questionId={question.id} />
                  </div>
                  <div className="mt-3">
                    <AskAssistantButton attemptId={attempt.id} questionId={question.id} />
                  </div>
                </div>
              )}
              {error && <p className="mt-3 text-sm text-danger-display">{error}</p>}

              <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
                <Button type="button" variant="secondary" disabled={index === 0} onClick={() => go(index - 1)}>
                  <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "exams.previous" })}
                </Button>
                {!full && question.chosen_choice === null ? (
                  <Button
                    type="button"
                    disabled={selected === null || saving}
                    onClick={() => selected !== null && save(question, { choice: selected })}
                  >
                    {intl.formatMessage({ id: "quizzes.check_answer" })}
                  </Button>
                ) : index < questions.length - 1 ? (
                  <Button type="button" onClick={() => go(index + 1)}>
                    {intl.formatMessage({ id: "exams.next" })} <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : full ? (
                  <Button type="button" onClick={() => setReviewing(true)}>
                    {intl.formatMessage({ id: "exams.review_label" }, partValues)}
                  </Button>
                ) : singleDone ? (
                  <Button asChild>
                    <Link href={`/exam_attempts/${attempt.id}/results`}>{intl.formatMessage({ id: "quizzes.see_results" })}</Link>
                  </Button>
                ) : null}
              </div>

              <QuestionNavigator questions={questions} current={index} onSelect={go} />
              {full && (
                <div className="mt-4">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setReviewing(true)}>
                    {intl.formatMessage({ id: "exams.review_and_submit" }, partValues)}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )
      )}
    </Layout>
  )
}

function Layout({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>{children}</AppShell>
    </>
  )
}

function QuestionNavigator({
  questions,
  current,
  onSelect,
}: {
  questions: ExamQuestion[]
  current: number
  onSelect: (index: number) => void
}) {
  const intl = useIntl()
  return (
    <nav className="mt-8" aria-label={intl.formatMessage({ id: "exams.nav_label" })}>
      <div className="flex flex-wrap gap-1.5">
        {questions.map((q, i) => (
          <button
            key={q.id}
            type="button"
            onClick={() => onSelect(i)}
            aria-current={i === current ? "step" : undefined}
            aria-label={intl.formatMessage({ id: "exams.nav_item" }, { number: i + 1, answered: String(q.chosen_choice !== null), flagged: String(q.flagged) })}
            className={cn(
              "relative inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border text-xs",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              q.chosen_choice !== null
                ? "border-accent bg-accent-faded text-accent-display"
                : "border-hairline text-ink-body hover:bg-surface",
              i === current && "ring-2 ring-accent ring-offset-1 ring-offset-page",
            )}
          >
            {i + 1}
            {q.flagged && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-signal" aria-hidden />}
          </button>
        ))}
      </div>
    </nav>
  )
}

/** Counts down locally, and checks in with the server so time only runs while the exam is open. */
function useCountdown(attempt: ExamAttemptState) {
  const timed = attempt.mode === "full_exam" && !attempt.on_break && attempt.time_remaining_seconds !== null
  const [seconds, setSeconds] = React.useState<number | null>(attempt.time_remaining_seconds)

  React.useEffect(() => setSeconds(attempt.time_remaining_seconds), [attempt.time_remaining_seconds])

  const heartbeat = React.useCallback(async () => {
    try {
      const response = await fetch(`/exam_attempts/${attempt.id}/heartbeat`, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      })
      if (!response.ok) return
      const data: ClockResponse = await response.json()
      if (data.finished && data.results_url) {
        router.visit(data.results_url)
        return
      }
      setSeconds(data.time_remaining_seconds)
    } catch {
      // Offline for a moment; the next heartbeat catches up.
    }
  }, [attempt.id])

  React.useEffect(() => {
    if (!timed) return
    const tick = window.setInterval(() => setSeconds((s) => (s === null ? s : Math.max(s - 1, 0))), 1000)
    const beat = window.setInterval(heartbeat, HEARTBEAT_MS)
    return () => {
      window.clearInterval(tick)
      window.clearInterval(beat)
    }
  }, [timed, heartbeat])

  // Time's up: the server submits the exam and sends us to results.
  React.useEffect(() => {
    if (timed && seconds === 0) heartbeat()
  }, [timed, seconds, heartbeat])

  return { seconds, sync: (s: number) => setSeconds(s) }
}
